import {
  useV2CreateExpenseCategoryMutation,
  useV2UpdateExpenseCategoryMutation,
  useV2DeleteExpenseCategoryMutation,
} from "@generated";
import type { V2ExpenseCategoryResource } from "@generated";
import type { ExpenseCategoryFormValues } from "../types";

// Build the JSON:API `data` body for create/update. The `account` relationship
// is inferred from auth server-side, so we omit it and cast (same pattern as the
// Currencies/BusinessUnits MSPAs). When "use resource rate" is on, the rate and
// markup are cleared and the expense is forced optional (mirrors the legacy form).
export const buildExpenseCategoryData = (
  values: ExpenseCategoryFormValues,
  id?: string
): V2ExpenseCategoryResource => {
  const useRR = values.useResourceRate;

  return {
    type: "expense-categories",
    ...(id ? { id: Number(id) } : {}),
    attributes: {
      name: values.name,
      rate: useRR ? null : values.rate ?? null,
      markup: useRR ? null : values.markup ?? null,
      "use-resource-rate": useRR,
      status: useRR ? "optional" : values.status,
    },
    relationships: {
      phase: values.phaseId
        ? { data: { type: "phases", id: Number(values.phaseId) } }
        : { data: null },
    },
  } as unknown as V2ExpenseCategoryResource;
};

export const useExpenseCategoryMutations = () => {
  const [create, createState] = useV2CreateExpenseCategoryMutation();
  const [update, updateState] = useV2UpdateExpenseCategoryMutation();
  const [destroy, destroyState] = useV2DeleteExpenseCategoryMutation();

  return {
    create: (values: ExpenseCategoryFormValues) =>
      create({ body: { data: buildExpenseCategoryData(values) } }).unwrap(),

    update: (id: string, values: ExpenseCategoryFormValues) =>
      update({
        id: Number(id),
        body: { data: buildExpenseCategoryData(values, id) },
      }).unwrap(),

    // Archive (soft-delete: the API stamps deleted_at; restore re-activates).
    archive: (id: string) => destroy({ id: Number(id) }).unwrap(),

    // `active` is not in the generated V2ExpenseCategoryResource schema; the cast
    // lets restore re-activate a soft-deleted record (same pattern as Currencies).
    restore: (id: string, name: string) =>
      update({
        id: Number(id),
        body: {
          data: {
            type: "expense-categories",
            id: Number(id),
            attributes: { name, active: true },
          } as unknown as V2ExpenseCategoryResource,
        },
      }).unwrap(),

    states: { createState, updateState, destroyState },
  };
};

export default useExpenseCategoryMutations;
